import { loadStripe } from '@stripe/stripe-js'
import classNames from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Badge,
  BadgeSize,
  BadgeType,
  Button,
  ButtonColor,
  CheckboxGroup,
  CheckboxType,
  NotificationType,
  TooltipInfo,
} from '../../../components'
import env from '../../../config/env'
import {
  AspectRatio,
  CampaignAddon,
  Collaboration,
  CollaborationAddon,
  CountryCode,
  CreatorQuality,
  VideoDuration,
} from '../../../gql/types'
import { usePricing } from '../../../hooks'
import { useCreateAddonsCheckout } from '../../../hooks/payments'
import {
  formatPriceInCents,
  formattedAddonsPrice,
  getVideoPricing,
} from '../../../utils/PricingUtil'
import { UrlBuilder } from '../../../utils/UrlBuilder'
import { useNotification } from '../../../utils/hooks'
import {
  mapAddonToAspectRatio,
  mapCampaignAddonsTypeToText,
  mapCampaignAddonsTypeToTextSimple,
  videoFormatAddonOptions,
} from '../../../utils/mappers'

const stripePromise = loadStripe(env.STRIPE_KEY)

export const mapAspectRatiosAddonToTextForBrand = (
  addons: CampaignAddon[] | [],
) => {
  const addonsAspectRatios: CampaignAddon[] | [] = addons?.filter(
    (addon) =>
      addon === CampaignAddon.vertical ||
      addon === CampaignAddon.square ||
      addon === CampaignAddon.portrait,
  )

  // if purchased already
  if (addonsAspectRatios && addonsAspectRatios.length > 0) {
    let addonsAspectRatiosText = addonsAspectRatios
      .map((addon) => {
        return mapCampaignAddonsTypeToTextSimple(addon)
      })
      .join(', ')

    return `You have purchased the ${addonsAspectRatiosText} add-on${addonsAspectRatios.length > 1 ? 's' : ''}.`
  }

  // if not purchased
  return `This add-on adds aspect ratio(s) to your final video. You'll receive both the original and the altered versions.`
}

type EditCampaignFormData = {
  quality: CreatorQuality
  selectedAddon: CampaignAddon[] | []
  defaultAspectRatio: AspectRatio
}

interface IProps {
  collaboration: Collaboration
  addons: CollaborationAddon[] | []
  open: boolean
  setOpen: (event: any) => void
}

// TO DO: Refactor this component to use the CheckboxGroup component with default format
export const OrderAspectRatiosAddon: React.FC<IProps> = ({
  collaboration,
  addons,
  open,
  setOpen,
}) => {
  const campaign = collaboration.campaign
  const { brand } = campaign
  const { addNotification } = useNotification()
  const { pricing } = usePricing()
  const [creditPayment, setCreditPayment] = useState(false)
  const [initiateCheckoutLoading, setInitiateCheckoutLoading] = useState(false)
  const { createAddonsCheckout, loading } = useCreateAddonsCheckout()
  const cancelButtonRef = useRef(null)
  const campaignAspectRatio = campaign.aspectRatio

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<EditCampaignFormData>({
    defaultValues: {
      quality: campaign.quality ?? CreatorQuality.regular,
      selectedAddon: [],
      defaultAspectRatio: campaignAspectRatio,
    },
  })

  const initiateCheckout = async () => {
    setInitiateCheckoutLoading(true)

    try {
      const { data } = await createAddonsCheckout({
        variables: {
          brandId: brand.id,
          input: {
            addons: watch('selectedAddon'),
            collaborationId: collaboration.id,
            successRedirectUrlPath: UrlBuilder.buildCampaignCollaboratorsUrl(
              brand.id,
              collaboration.campaign.id,
            ),
            cancelRedirectUrlPath: UrlBuilder.buildCampaignCollaboratorsUrl(
              brand.id,
              collaboration.campaign.id,
            ),
          },
        },
      })

      // if no stripe session it means the purchase was made with credits
      if (!data?.createAddonsCheckout?.stripeSessionId) {
        addNotification(
          `Your add-on order has been confirmed.`,
          NotificationType.success,
        )
      }

      // if stripe session id present - redirect to stripe
      if (data?.createAddonsCheckout?.stripeSessionId) {
        // if stripe session id present - redirect to stripe
        const stripe = await stripePromise

        // When the customer clicks on the button, redirect them to Checkout.
        const result = await stripe.redirectToCheckout({
          sessionId: data?.createAddonsCheckout?.stripeSessionId,
        })

        if (result.error) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          addNotification(
            `Something went wrong. Please try again or contact support at hello@useclip.com.`,
            NotificationType.error,
          )
        }
      }

      setOpen(false)
      reset()
    } catch (error) {
      addNotification(
        // `Something went wrong. Please try again or contact support at hello@useclip.com.`,
        error.message,
        NotificationType.error,
      )
    }

    setInitiateCheckoutLoading(false)
  }

  const renderAddonsPrice = () => {
    if (!watch('selectedAddon')) {
      return null
    }

    const formattedPrice = formattedAddonsPrice({
      addons: watch('selectedAddon'),
      currency: campaign.currency,
      duration: campaign.videoDuration ?? VideoDuration.half,
      pricing,
    })

    return (
      <div className='border rounded-lg shadow-sm px-3 py-3 flex items-center'>
        <div className='flex flex-row justify-between items-center text-sm w-full'>
          <div className='font-medium text-gray-500 ml-1'>
            {formattedPrice}
            {brand.countryCode === CountryCode.GB && <span>{` `}+ VAT</span>}
          </div>
          <div>
            <Badge
              type={BadgeType.indigo}
              iconPosition='left'
              text={
                <TooltipInfo
                  valueKey='addons-badge'
                  content={`${watch('selectedAddon').length} add-on(s)`}
                  tooltipText={mapAspectRatiosAddonToTextForBrand(
                    watch('selectedAddon').map(
                      (addon) => addon.description as unknown as CampaignAddon,
                    ),
                  )}
                />
              }
              size={BadgeSize.small}
              className='!mr-0'
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPayWithCreditInput = () => {
    if (campaign.currency !== brand.currency) {
      return null
    }

    if (brand.creditCents <= 0) {
      return null
    }

    const formattedCredit = formatPriceInCents(
      {
        priceInCents: brand.creditCents,
        currency: campaign.currency,
      },
      { minimumFractionDigits: 0 },
    )

    return (
      <div className='relative flex items-start mt-3'>
        <div className='flex h-6 items-center'>
          <input
            id='payWithCredit'
            aria-describedby='payWithCredit-description'
            name='payWithCredit'
            type='checkbox'
            // checked={creditPayment}
            checked={true}
            disabled
            onChange={() => setCreditPayment((prev) => !prev)}
            className={classNames(
              'h-4 w-4 rounded border-gray-300 text-gray-600 disabled:accent-gray-300 focus:ring-gray-500 cursor-pointer',
              creditPayment
                ? 'focus:ring-gray-800 accent-primary-800'
                : 'accent-gray-300',
            )}
          />
        </div>
        <div className='ml-3 text-sm leading-6'>
          <label
            htmlFor='payWithCredit'
            className='font-medium text-gray-900 cursor-pointer'
          >
            Use credits
          </label>
          <label
            htmlFor='payWithCredit'
            id='payWithCredit-description'
            className='block leading-4 text-gray-500 cursor-pointer'
          >
            You have {formattedCredit} remaining credits. Credits will
            automatically be applied.
          </label>
        </div>
      </div>
    )
  }

  const videoPricing = getVideoPricing({
    currency: collaboration.campaign.currency,
    videoDuration: collaboration.campaign.videoDuration,
    pricing,
  })

  const options = videoFormatAddonOptions(
    videoPricing.addons,
    collaboration.campaign.currency,
  )

  // Extract addon descriptions from existing addons
  const existingAddonDescriptions = useMemo(
    () => addons?.map((addon) => addon.description as CampaignAddon) || [],
    [addons],
  )

  // Calculate filtered options excluding both campaignAspectRatio and existing addons
  const filteredOptions = useMemo(() => {
    return options.filter(
      (option) =>
        mapAddonToAspectRatio(option.type) !== campaignAspectRatio &&
        !existingAddonDescriptions.includes(option.type),
    )
  }, [options, campaignAspectRatio, existingAddonDescriptions])

  // Keep only this effect for handling landscape case
  useEffect(() => {
    if (campaignAspectRatio === AspectRatio.landscape) {
      setValue('selectedAddon', [])
    }
  }, [campaignAspectRatio])

  let typesDisabled: CampaignAddon[] = []
  addons?.forEach((addon: { description: CampaignAddon }) => {
    if (addon.description === CampaignAddon.vertical) {
      typesDisabled.push(CampaignAddon.vertical)
    }
    if (addon.description === CampaignAddon.square) {
      typesDisabled.push(CampaignAddon.square)
    }
    if (addon.description === CampaignAddon.portrait) {
      typesDisabled.push(CampaignAddon.portrait)
    }
  })

  return (
    <form onSubmit={handleSubmit(initiateCheckout)}>
      <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 h-[480px] overflow-y-auto'>
        <div className='sm:flex sm:items-start'>
          <div className='mt-3 sm:mt-0 sm:ml-0 sm:text-left'>
            <h3 className='text-lg leading-6 font-medium text-gray-900'>
              Aspect Ratio
            </h3>
            <div className='p-2 -mx-2'>
              <p className='text-sm text-gray-500'>
                Each aspect ratio is a different format of the video, optimized
                for different social media platforms.
              </p>
            </div>
            <div className='p-2 -mx-2'>
              {addons && addons.length > 0 && (
                <p className='text-sm text-gray-500'>
                  You've already purchased the following add-on
                  {addons.length > 1 ? 's' : ''} for this collaboration:{' '}
                  <b>
                    <i>
                      {addons
                        .map((addon) =>
                          mapCampaignAddonsTypeToText(addon.description),
                        )
                        .join(', ')}
                    </i>
                  </b>
                </p>
              )}

              {campaignAspectRatio === AspectRatio.landscape && (
                <p className='mt-4 text-sm leading-5 font-medium text-gray-700'>
                  No additional formats available for landscape videos (16:9).
                  Due to the significant difference between landscape and the
                  other formats, creators cannot easily adapt between them. For
                  both formats, please create separate campaigns.
                </p>
              )}

              {(!addons || addons?.length < 2) &&
                campaignAspectRatio !== AspectRatio.landscape && (
                  <>
                    <p className='mt-4 text-sm leading-5 font-medium text-gray-700'>
                      Additional Formats
                    </p>
                    <CheckboxGroup
                      selected={watch('selectedAddon')}
                      {...register('selectedAddon')}
                      className='grid grid-cols-2'
                      error={errors?.selectedAddon?.message}
                    >
                      {filteredOptions.map(
                        ({ name, type, description, icon }, index) => {
                          return (
                            <CheckboxGroup.CheckboxItem
                              type={CheckboxType.checkbox}
                              key={index}
                              value={type}
                              title={name}
                              icon={icon}
                              subtitle={description as unknown as string}
                              {...register('selectedAddon')}
                              className='!my-2 !mr-2'
                              disabled={typesDisabled.includes(type)}
                              inputView={false}
                            />
                          )
                        },
                      )}
                    </CheckboxGroup>
                  </>
                )}
            </div>
            {addons && addons.length === 2 && (
              <div className='px-3 pt-[99px] flex justify-start items-center'></div>
            )}
            {(!addons || addons.length < 2) &&
              campaignAspectRatio !== AspectRatio.landscape && (
                <>
                  <div className='w-full items-center'>
                    {renderAddonsPrice()}
                  </div>
                  {renderPayWithCreditInput()}
                </>
              )}
          </div>
        </div>
      </div>
      <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
        <Button
          colour={ButtonColor.primary}
          title='Confirm your order'
          type='submit'
          loading={initiateCheckoutLoading}
          disabled={
            campaignAspectRatio === AspectRatio.landscape ||
            (addons && addons.length === 2) ||
            !watch('selectedAddon')
              ? true
              : false
          }
          className='w-full sm:w-auto'
        />
        <button
          type='button'
          className='mt-3 mr-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
          onClick={() => setOpen(false)}
          ref={cancelButtonRef}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
